import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Clock3, Play, FileText } from 'lucide-react';
import { useItinerary } from '../hooks/useData';
import { getImageUrl } from '../utils/getImageUrl';
import './Roteiro.css';

const Roteiro = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { data: roteiro, loading, error } = useItinerary(id || '');
    const [currentSlide, setCurrentSlide] = useState(0);

    const images = roteiro?.gallery?.length
        ? roteiro.gallery
        : roteiro?.cover_image
            ? [roteiro.cover_image]
            : [];

    const prevSlide = useCallback(() => {
        setCurrentSlide(prev => (prev === 0 ? images.length - 1 : prev - 1));
    }, [images.length]);

    const nextSlide = useCallback(() => {
        setCurrentSlide(prev => (prev === images.length - 1 ? 0 : prev + 1));
    }, [images.length]);

    if (loading) {
        return (
            <div className="roteiro-page">
                <div className="roteiro-loading">
                    <div className="spinner" />
                    <span>Carregando roteiro...</span>
                </div>
            </div>
        );
    }

    if (error || !roteiro) {
        return (
            <div className="roteiro-page">
                <div className="roteiro-error">
                    <h2>Roteiro não encontrado</h2>
                    <p>O roteiro que você procura não existe ou foi removido.</p>
                    <button type="button" onClick={() => navigate('/')}>Voltar ao Início</button>
                </div>
            </div>
        );
    }

    return (
        <div className="roteiro-page">
            <div className="roteiro-hero">
                <button className="back-btn" onClick={() => navigate(-1)} aria-label="Voltar">
                    <ArrowLeft size={22} />
                </button>

                {images.length > 0 ? (
                    <div className="carousel-wrapper">
                        <div
                            className="carousel-track"
                            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                        >
                            {images.map((img, idx) => (
                                <div className="carousel-slide" key={idx}>
                                    <img src={getImageUrl(img)} alt={`${roteiro.title} - Foto ${idx + 1}`} />
                                </div>
                            ))}
                        </div>

                        {images.length > 1 && (
                            <>
                                <button className="carousel-btn prev" onClick={prevSlide} aria-label="Anterior">
                                    <ChevronLeft size={20} />
                                </button>
                                <button className="carousel-btn next" onClick={nextSlide} aria-label="Próximo">
                                    <ChevronRight size={20} />
                                </button>
                                <div className="carousel-dots">
                                    {images.map((_, idx) => (
                                        <button
                                            key={idx}
                                            className={`carousel-dot ${idx === currentSlide ? 'active' : ''}`}
                                            onClick={() => setCurrentSlide(idx)}
                                            aria-label={`Ir para foto ${idx + 1}`}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    <div style={{
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(135deg, #0ea5a4 0%, #06b6d4 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'rgba(255,255,255,0.4)',
                        fontSize: '64px',
                        fontWeight: 800,
                    }}>
                        {roteiro.title.charAt(0)}
                    </div>
                )}
            </div>

            <div className="roteiro-content">
                <h1 className="roteiro-title">{roteiro.title}</h1>
                {roteiro.subtitle && <p className="roteiro-subtitle">{roteiro.subtitle}</p>}

                <div className="roteiro-meta">
                    {roteiro.duration && (
                        <span className="roteiro-meta-item">
                            <Clock3 size={16} />
                            {roteiro.duration}
                        </span>
                    )}
                </div>

                {roteiro.video_url && (
                    <div className="roteiro-video-section">
                        <h3><Play size={18} /> Vídeo do Passeio</h3>
                        <div className="roteiro-video-container">
                            <video src={getImageUrl(roteiro.video_url)} controls preload="metadata" />
                        </div>
                    </div>
                )}

                {roteiro.description && (
                    <div className="roteiro-description-section">
                        <h3><FileText size={18} /> Sobre o Passeio</h3>
                        <p className="roteiro-description-text">{roteiro.description}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Roteiro;
